/**
 * DeactivateConfirmModal
 *
 * GitHub-style confirmation modal for deactivating entities.
 * Requires typing the exact entity name to confirm.
 */

import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { CascadeImpactDisplay, CascadeImpact } from './index';

export type EntityType = 'organization' | 'channel_partner' | 'enterprise' | 'user';

interface DeactivateConfirmModalProps {
  isOpen: boolean;
  entityType: EntityType;
  entityName: string;
  cascadeImpact?: CascadeImpact;
  isLoading?: boolean;
  onConfirm: (confirmName: string) => void;
  onCancel: () => void;
}

const entityTypeLabels: Record<EntityType, string> = {
  organization: 'Organization',
  channel_partner: 'Channel Partner',
  enterprise: 'Enterprise',
  user: 'User'
};

export const DeactivateConfirmModal: React.FC<DeactivateConfirmModalProps> = ({
  isOpen,
  entityType,
  entityName,
  cascadeImpact,
  isLoading = false,
  onConfirm,
  onCancel
}) => {
  const [confirmInput, setConfirmInput] = useState('');
  const isMatch = confirmInput === entityName;
  const label = entityTypeLabels[entityType];

  // Reset input when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmInput('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (isMatch) {
      onConfirm(confirmInput);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isMatch && !isLoading) {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Deactivate {label}?
              </h2>
            </div>
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Warning message */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action will deactivate{' '}
                <span className="font-semibold">{entityName}</span>
                {entityType !== 'user' && ' and all associated entities'}.
                The data will be preserved for compliance but will no longer be accessible.
              </p>
            </div>

            {/* Cascade impact */}
            {cascadeImpact && (
              <CascadeImpactDisplay impact={cascadeImpact} showWarning={false} />
            )}

            {/* Confirmation input */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-700">
                To confirm, type{' '}
                <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">
                  {entityName}
                </span>{' '}
                below:
              </label>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Type ${entityType === 'user' ? 'email' : 'name'} to confirm`}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                autoFocus
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isMatch || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Deactivating...
                </span>
              ) : (
                `I understand, deactivate this ${label.toLowerCase()}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
